import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { RouteComponentProps } from "react-router";
import { generatePath, withRouter } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import TrainerStore from "stores/TrainerStore";
import LayoutContainer from "components/Layout/Container";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { SNACKBAR_TYPES } from "components/Snackbar";
import ROUTES from "navigation/routes";
import { TableSortType } from "stores/templates/TableStore";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  CardWrapper,
  OverflowLoader,
  ScrollLoader,
} from "./styles";

import TrainerCard from "./TrainerCard";
import { constants } from "./constants";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class TargetZone extends React.Component<Props> {
  @computed get items() {
    return TrainerStore.items;
  }

  @computed get isListEmpty() {
    return this.items.length === 0;
  }

  componentDidMount() {
    TrainerStore.fetch();
  }

  componentWillUnmount() {
    TrainerStore.resetPage();
  }

  fetchMoreItems = async () => {
    const { setMessage } = this.props;

    try {
      await TrainerStore.fetchMore();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    TrainerStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH && TrainerStore.fetch();
  };

  onSearchClear = () => {
    TrainerStore.clearSearch();
    TrainerStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    TrainerStore.applyOrder(value);
    TrainerStore.resetPage();
    TrainerStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      TrainerStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    TrainerStore.resetPage();
    TrainerStore.fetch();
  };

  onFilterClear = () => {
    TrainerStore.clearFilters();
    TrainerStore.fetch();
  };

  onAddItemClick = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_TRAINERS_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (item: any) => () => {
    const path = generatePath(ROUTES.CONTENT_TRAINERS_DETAILS, {
      id: item.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (item: any) => () => {
    const path = generatePath(ROUTES.CONTENT_TRAINERS_DETAILS, {
      id: item.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onPublish = (item: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = item;

    try {
      await TrainerStore.changeStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <LayoutContainer header={<Header controls={<PublishButton />} />}>
        <Page
          title={constants.targetZone.title}
          controls={
            <>
              <PageControl>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={this.onAddItemClick}
                >
                  {constants.targetZone.addButtonTitle}
                </Button>
              </PageControl>
            </>
          }
          isLoading={TrainerStore.isTrainerLoading}
        >
          <SearchContainer>
            <Search
              initValue={TrainerStore.search}
              onApply={this.onSearchApply}
              onClear={this.onSearchClear}
            />
            <SearchControls>
              <SearchControlWrapper>
                <SortingControl
                  initValue={TrainerStore.orderContext.appliedOrderInfo}
                  onApply={this.onSortApply}
                />
              </SearchControlWrapper>
              <SearchControlWrapper>
                <FilteringControl
                  initValue={TrainerStore.filters}
                  onApply={this.onFilterApply}
                  onClear={this.onFilterClear}
                  statusFiltering
                />
              </SearchControlWrapper>
            </SearchControls>
          </SearchContainer>

          {(TrainerStore.isTrainerLoading ||
            TrainerStore.isTrainerPublishing) && <OverflowLoader />}
          {!this.isListEmpty ? (
            <InfiniteScroll
              dataLength={this.items.length}
              hasMore={TrainerStore.hasMoreInfo}
              next={this.fetchMoreItems}
              height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
              loader={<ScrollLoader />}
            >
              {this.items.map(item => (
                <CardWrapper key={item.id}>
                  <TrainerCard
                    title={item.title}
                    image={item.photo}
                    publishStatus={item.publishStatus}
                    onDetailsClick={this.onDetailsClick(item)}
                    onEdit={this.onEdit(item)}
                    onPublish={this.onPublish(item)}
                  />
                </CardWrapper>
              ))}
            </InfiniteScroll>
          ) : (
            <Placeholder
              height={280}
              width={280}
              text={
                TrainerStore.search
                  ? constants.targetZone.noRecordsMatch
                  : constants.targetZone.noRecords
              }
            />
          )}
        </Page>
      </LayoutContainer>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(TargetZone)));
